import { Button } from "@ui/Button";
import type { AppLayoutKind } from "@ui/layouts/layoutProvider";
import { addClipToBoard, getBoard, getBoardClip } from "@/client";
import { buildAddClipBody } from "~/modules/shared/boards/addToBoardSave";
import type { EditClipEventDetail } from "~/modules/shared/chat/components/ChatLayout/ChatLayout";
import { toast } from "../Toasts/toast";

declare const pageVars: {
	agencyPublicId?: string;
};

type Props = {
	/** GVT Labs video public ID. */
	video_id: string;
	/** Clip start in seconds, or null to let the API default to 0. */
	start_time?: number | null;
	/** Clip end in seconds, or null to let the API default to the video duration from the database. */
	end_time?: number | null;
	/** Optional user trim start (seconds). */
	clipped_start?: number | null;
	/** Optional user trim end (seconds). */
	clipped_end?: number | null;
	/** Video duration in seconds. */
	video_duration: number;
	/** Clip duration in seconds. */
	clip_duration: number;
	/** From `useLayoutAppKind()` in a component body, or a fixed value (e.g. `"askgvt"` in chat). */
	appKind: AppLayoutKind;
	projectId?: string;
	agencyId?: string;
	boardId?: string;
	/** LangChain clip summary when the clip came from a question source. */
	title?: string | null;
};

export function clipContextForAppKind(
	kind: AppLayoutKind,
): "askgvt" | "gvtlabs" | "roster" {
	if (kind === "agency-hub") {
		return "roster";
	}
	if (kind === "askgvt" || kind === "studio") {
		return "askgvt";
	}
	return "gvtlabs";
}

export function agencyIdForBoardContext(
	context: ReturnType<typeof clipContextForAppKind>,
	agencyId?: string,
): string | undefined {
	if (context !== "roster") {
		return undefined;
	}
	return agencyId ?? pageVars.agencyPublicId;
}

export const useAddToBoard = async (props: Props) => {
	// Allow 10 seconds of padding around the clip start and end times, to enable future clipping
	const clipStart = props.start_time ?? 0;
	const clipEnd = props.end_time ?? clipStart + props.clip_duration;
	const context = clipContextForAppKind(props.appKind);
	const agencyId = agencyIdForBoardContext(context, props.agencyId);
	const body = buildAddClipBody({
		videoId: props.video_id,
		boardId: props.boardId ?? null,
		moveClipTrim: { start: clipStart, end: clipEnd },
		videoDurationSeconds: props.video_duration,
		context,
		projectId: props.projectId,
		agencyId,
		title: props.title ?? undefined,
	});

	const response = await addClipToBoard({
		body,
	});
	if (response.response.status === 409) {
		return { duplicate: true as const, response };
	}
	if (response.response.status === 200) {
		const board = await getBoard({
			path: {
				board_id: String(response.data.board_id),
			},
		});

		toast.success({
			symbol: "bookmark_add",
			title: `Clip added to "${board.data.title}"`,
			description: () => (
				<Button
					variant="secondary"
					theme="white"
					symbol="settings"
					symbolRelativeSize="even-larger"
					onClick={async () => {
						// Need to re-request the clip to get the signed URLs
						const newClip = await getBoardClip({
							path: {
								board_id: board.data.id,
								clip_id: response.data.id,
							},
							query: { num_frames: 50 },
						});
						if (newClip.error) {
							toast.error({
								title: "Failed to get clip",
								description: (newClip.error as { message?: string }).message,
							});
							return;
						}
						const moveEvent: EditClipEventDetail = {
							type: "open_move_board_panel",
							clip: newClip.data,
							video: {
								id: props.video_id,
							},
							board: board.data,
						};
						document.dispatchEvent(
							new CustomEvent("open_move_board_panel", {
								detail: moveEvent,
							}),
						);
					}}
				>
					Edit
				</Button>
			),
		});
		return { duplicate: false as const, response, board };
	}
	toast.error({
		title: "Failed to add clip to board",
	});
	return { duplicate: false as const, response, board: null };
};
